import { useMemo } from 'react';
import { SearchableSelect } from './searchable-select';
import type { Option } from './searchable-select';
import { useActiveDesignations } from '../../services/httpServices/queries';
import { AlertCircle } from 'lucide-react';

interface DesignationSearchSelectProps {
  value: string;
  onChange: (designation: string) => void;
  label?: React.ReactNode;
  required?: boolean;
  error?: React.ReactNode;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
}

/**
 * Searchable dropdown of active designations, sourced from the HR Mgmt. →
 * Designations registry. The designation **name** is stored on the referral
 * partner (the API models `designation` as a plain string), so existing
 * free-text values keep working while new ones are picked from the registry.
 */
export function DesignationSearchSelect({
  value,
  onChange,
  label,
  required = false,
  error,
  disabled = false,
  placeholder = 'Search designation…',
  id,
}: DesignationSearchSelectProps) {
  const { data, isLoading } = useActiveDesignations();
  const designations = useMemo(() => data?.data ?? [], [data]);

  const options = useMemo<Option[]>(() => {
    const base: Option[] = designations.map((designation) => ({
      value: designation.name,
      label: designation.name,
      description: designation.designationCode,
      keywords: [designation.designationCode, designation.description].filter(Boolean) as string[],
    }));

    // Keep a stored value visible even if it is no longer an active
    // designation (e.g. a legacy free-text value) so editing never blanks it.
    if (value && !base.some((o) => o.value === value)) {
      base.unshift({ value, label: value, description: 'Not in active designations' });
    }
    return base;
  }, [designations, value]);

  return (
    <SearchableSelect
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      loading={isLoading}
      disabled={disabled}
      id={id}
      label={
        label !== undefined ? (
          <>
            {label}
            {required && <span className="text-danger"> *</span>}
          </>
        ) : undefined
      }
      labelClassName="block text-xs font-medium text-text-muted mb-1"
      error={
        error ? (
          <p className="flex items-center gap-1 mt-1 text-xs text-danger">
            <AlertCircle size={12} />
            {error}
          </p>
        ) : undefined
      }
    />
  );
}
